import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, To, useBlocker, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { faMagnifyingGlass, faPlus } from '@fortawesome/pro-regular-svg-icons';
import Button from '../../../core/components/Button.tsx';
import ConfirmModal from '../../../core/components/ConfirmModal.tsx';
import StringField from '../../../core/components/Fields/StringField.tsx';
import { withIcon } from '../../../core/components/Icon.tsx';
import Loader from '../../../core/components/Loader.tsx';
import { SettingsCard } from '../../../core/components/SettingsFormCard.tsx';
import TablePagination from '../../../core/components/TablePagination.tsx';
import { url } from '../../../core/utils/link.ts';
import { DocumentType, mapDocumentTypeToGqlDocumentType } from '../../constants.ts';
import { useGetFieldLabel } from '../../llmExtractorFields.ts';
import { FieldDefinition } from '../fieldsConfigData.ts';
import RuleEditor from './RuleEditor.tsx';
import RuleRow from './RuleRow.tsx';
import { getRuleScopeTitle, NEW_RULE_ID, Rule, RULE_LEVELS, RuleLevel, useRules } from './rules.ts';

const PlusIcon = withIcon(faPlus);
const SearchIcon = withIcon(faMagnifyingGlass);

const PAGE_SIZE = 10;

const LEVELS: RuleLevel[] = ['header', 'lineItem'];

type LinkTo = (ruleId: string | null, params?: Record<string, string | undefined>) => To;

interface RuleSectionProps {
    level: RuleLevel;
    rules: Rule[];
    linkTo: LinkTo;
}

// the rules of one level in their own card, with their own search and pages in the URL
const RuleSection = ({ level, rules, linkTo }: RuleSectionProps) => {
    const { t } = useTranslation('config');
    const getFieldLabel = useGetFieldLabel();
    const [searchParams, setSearchParams] = useSearchParams();
    const prefix = RULE_LEVELS[level].param;
    const search = searchParams.get(`${prefix}-search`) ?? '';
    const activePage = Number(searchParams.get(`${prefix}-page`)) || 1;
    const itemsPerPage = Number(searchParams.get(`${prefix}-page-size`)) || PAGE_SIZE;
    const setListParams = (values: Record<string, string | number | null>) =>
        setSearchParams(
            (params) => {
                Object.entries(values).forEach(([name, value]) =>
                    value ? params.set(`${prefix}-${name}`, String(value)) : params.delete(`${prefix}-${name}`)
                );
                return params;
            },
            { replace: true }
        );

    const needle = search.trim().toLowerCase();
    const matches = (rule: Rule) =>
        [
            rule.name ?? '',
            getRuleScopeTitle(rule, t),
            ...(rule.partitionIds ?? []),
            ...(rule.fields ?? []),
            ...(rule.fields ?? []).map(getFieldLabel),
            rule.prompt ?? '',
        ]
            .join(' ')
            .toLowerCase()
            .includes(needle);
    const shown = needle ? rules.filter(matches) : rules;
    const page = shown.slice((activePage - 1) * itemsPerPage, activePage * itemsPerPage);

    return (
        <SettingsCard
            className="pb-10"
            title={t(RULE_LEVELS[level].titleKey)}
            description={t(RULE_LEVELS[level].descriptionKey)}
            controls={
                <Button asChild variant="outline">
                    <Link to={linkTo(NEW_RULE_ID, { level: prefix })}>
                        <PlusIcon /> {t('configPage.fineTuning.addRule')}
                    </Link>
                </Button>
            }
        >
            {rules.length > 0 && (
                <div className="flex items-center gap-3">
                    <StringField
                        className="flex-1"
                        prefix={<SearchIcon />}
                        value={search}
                        onValueChange={(value) => setListParams({ search: value, page: null })}
                        placeholder={t('configPage.fineTuning.searchPlaceholder')}
                    />
                    <span className="flex-none text-xs text-content-medium">
                        {needle
                            ? t('configPage.fineTuning.searchCount', { shown: shown.length, total: rules.length })
                            : t('configPage.fineTuning.ruleCount', { count: rules.length })}
                    </span>
                </div>
            )}
            {!rules.length && <p className="text-sm text-content-medium">{t('configPage.fineTuning.empty')}</p>}
            {rules.length > 0 && !shown.length && (
                <p className="text-sm text-content-medium">{t('configPage.fineTuning.noMatch', { search })}</p>
            )}
            {page.length > 0 && (
                <div className="flex flex-col gap-2">
                    {page.map((rule) => (
                        <RuleRow key={rule.id} rule={rule} to={linkTo(rule.id)} />
                    ))}
                </div>
            )}
            {shown.length > 0 && (
                <TablePagination
                    totalCount={shown.length}
                    activePage={activePage}
                    itemsPerPage={itemsPerPage}
                    setActivePage={(value) => setListParams({ page: value > 1 ? value : null })}
                    setItemsPerPage={(value) =>
                        setListParams({ 'page-size': value === PAGE_SIZE ? null : value, page: null })
                    }
                />
            )}
        </SettingsCard>
    );
};

interface FineTuningRulesProps {
    channelId: string;
    documentType: DocumentType;
    configPath: string;
    channelFields: FieldDefinition[];
}

// The fine-tuning rules of both levels: a list per level, and each rule on its own page. The URL holds what is open:
// /<ruleId> a rule, /new?level= a new rule, /new?copy=<ruleId> a copy; a new rule or a copy is a draft until it is
// saved
const FineTuningRules = ({ channelId, documentType, configPath, channelFields }: FineTuningRulesProps) => {
    const { t } = useTranslation('config');
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { tab, configId } = useParams();
    const gqlDocumentType = mapDocumentTypeToGqlDocumentType[documentType];
    const hasChangesRef = useRef(false);
    // leaving a rule with unsaved edits in any way (the breadcrumb, Copy, the sidebar, the browser's Back) asks first
    const blocker = useBlocker(
        ({ currentLocation, nextLocation }) =>
            hasChangesRef.current &&
            (currentLocation.pathname !== nextLocation.pathname || currentLocation.search !== nextLocation.search)
    );

    const headerRules = useRules('header', channelId, gqlDocumentType);
    const lineItemRules = useRules('lineItem', channelId, gqlDocumentType);
    const queries = { header: headerRules, lineItem: lineItemRules };
    const levelOf = (ruleId: string | null) =>
        LEVELS.find((level) => queries[level].rules.some(({ id }) => id === ruleId));

    const copyOf = configId === NEW_RULE_ID ? searchParams.get('copy') : null;
    const openLevel =
        configId === NEW_RULE_ID
            ? (levelOf(copyOf) ?? LEVELS.find((level) => RULE_LEVELS[level].param === searchParams.get('level')))
            : levelOf(configId ?? null);

    // the lists' search and pages stay in the URL while a rule is open, so going back restores them
    const linkTo: LinkTo = (ruleId, params = {}) => {
        const search = new URLSearchParams(searchParams);
        search.delete('level');
        search.delete('copy');
        Object.entries(params).forEach(([name, value]) => value && search.set(name, value));
        return { pathname: url(configPath, { channelId, tab, configId: ruleId }), search: search.toString() };
    };
    const backToList = () => navigate(linkTo(null));

    if (headerRules.loading || lineItemRules.loading) {
        return (
            <SettingsCard title={t('configPage.fineTuning.title')}>
                <Loader />
            </SettingsCard>
        );
    }

    if (configId && openLevel) {
        return (
            <>
                <RuleEditor
                    level={openLevel}
                    channelId={channelId}
                    gqlDocumentType={gqlDocumentType}
                    channelFields={channelFields}
                    rules={queries[openLevel].rules}
                    ruleId={configId}
                    copyOf={copyOf}
                    parents={[
                        { label: t('configPage.fineTuning.title'), onClick: backToList },
                        { label: t(RULE_LEVELS[openLevel].titleKey), onClick: backToList },
                    ]}
                    hasChangesRef={hasChangesRef}
                    onBack={backToList}
                    onOpen={(ruleId) => navigate(linkTo(ruleId), { replace: true })}
                    onRefetch={queries[openLevel].refetch}
                    onCopy={() => navigate(linkTo(NEW_RULE_ID, { copy: configId }))}
                    getRuleUrl={(otherChannelId, ruleId) =>
                        url(configPath, { channelId: otherChannelId, tab, configId: ruleId })
                    }
                />
                {blocker.state === 'blocked' && (
                    <ConfirmModal
                        title={t('configPage.document.discardTitle')}
                        labelConfirm={t('configPage.document.discardConfirm')}
                        onConfirm={() => blocker.proceed()}
                        onCancel={() => blocker.reset()}
                        danger
                    >
                        {t('configPage.fineTuning.discardMessage')}
                    </ConfirmModal>
                )}
            </>
        );
    }

    return (
        <div className="flex flex-col gap-7">
            {LEVELS.map((level) => (
                <RuleSection key={level} level={level} rules={queries[level].rules} linkTo={linkTo} />
            ))}
        </div>
    );
};

export default FineTuningRules;
